import * as React from "react";
import {
  Radio,
  RadioChangeEvent,
  Upload,
  Button,
  Divider,
  message,
  Space,
  Tooltip,
} from "antd";
import type { UploadProps } from "antd";
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import styled from "styled-components";

import { RoseUserSettings } from "@types";
import { ThemeVarNames, Theme } from "@theme";
import { useUserSettings } from "@service/useUserSettings";

import { ThemeSettingsCard } from "../SettingsContainer";

/**
 * ThemeSettings
 * - Allows switching between themes.
 * - Allows uploading / removing a watermark image per theme.
 *
 * IMPORTANT (Backend contract):
 * `changeSettings` expects a FormData instance (see useUserSettings hook).
 * For theme changes we send FormData with only: (theme=<chosenTheme>)
 * For watermark uploads we send the file under the key: <theme>_watermark (e.g. "light_watermark")
 * For watermark removal we send the same key with an empty string to indicate deletion.
 *
 * The backend is assumed to merge partial updates and respond with the full RoseUserSettings
 * (which then repopulates `settings` in the user container).
 */
export function ThemeSettings(): JSX.Element {
  return (
    <ThemeSettingsCard title="Theme">
      <ThemeSwitch />
      <Divider style={{ margin: "24px 0 16px" }} />
      <ThemeWatermarkUploader />
    </ThemeSettingsCard>
  );
}

/* ------------------------------------------------------------------------------------------ */
/* THEME SWITCH                                                                               */
/* ------------------------------------------------------------------------------------------ */

function ThemeSwitch(): JSX.Element {
  const { settings, changeSettings } = useUserSettings();

  const handleOnChange = (event: RadioChangeEvent) => {
    const chosenTheme = event.target.value as Theme;

    // Build FormData with only the changed field
    const fd = new FormData();
    fd.append("theme", chosenTheme);

    changeSettings(fd);
  };

  const options = Theme.getAllThemes().map((theme) => ({
    label: (
      <StyledLabelContainer aria-label="label-container">
        <Skeleton theme={theme} />
        <StyledLabelText>
          {`${Theme.friendlyThemeName(theme)} Mode`}
        </StyledLabelText>
      </StyledLabelContainer>
    ),
    value: theme,
  }));

  return (
    <StyledContainer>
      <StyledRadioGroup
        onChange={handleOnChange}
        value={settings.theme}
        optionType="button"
        options={options}
      />
    </StyledContainer>
  );
}

/* ------------------------------------------------------------------------------------------ */
/* WATERMARK UPLOADER                                                                         */
/* ------------------------------------------------------------------------------------------ */

function ThemeWatermarkUploader(): JSX.Element {
  const { settings, changeSettings } = useUserSettings();
  const currentTheme = settings.theme;
  const watermarkKey = `${currentTheme}_watermark` as const;

  // The backend returns user settings. We assume it persists some representation
  // (URL, base64, etc.) under the same field name.
  const serverWatermarkValue = (settings as Record<string, string | undefined>)[
    watermarkKey
  ];

  // Local preview overrides what server has until new settings arrive
  const [localPreview, setLocalPreview] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState(false);

  // Keep local preview in sync if server pushes a change (e.g. after mutation success)
  React.useEffect(() => {
    // If server watermark disappears (removed), clear local preview.
    if (!serverWatermarkValue) {
      setLocalPreview(null);
    }
  }, [serverWatermarkValue]);

  const effectivePreview = localPreview || serverWatermarkValue || null;

  const sendWatermarkFile = async (file: File) => {
    setUploading(true);
    try {
      // Create an object URL for optimistic preview
      const objectUrl = URL.createObjectURL(file);
      setLocalPreview(objectUrl);

      const fd = new FormData();
      // Append the raw file under the theme-specific key
      fd.append(watermarkKey, file);

      changeSettings(fd);
      message.success("Watermark uploaded");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      message.error("Failed to upload watermark");
      setLocalPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const clearWatermark = () => {
    const fd = new FormData();
    // Convention: sending empty string for key indicates deletion
    fd.append(watermarkKey, "");
    changeSettings(fd);
    setLocalPreview(null);
    message.success("Watermark removed");
  };

  const beforeUpload: UploadProps["beforeUpload"] = (file) => {
    const allowedTypes = [
      "image/png",
      "image/jpeg",
      "image/svg+xml",
      "image/webp",
    ];
    if (!allowedTypes.includes(file.type)) {
      message.error("Only PNG, JPG, SVG or WEBP images are allowed");
      return Upload.LIST_IGNORE;
    }
    const maxBytes = 250 * 1024; // 250KB
    if (file.size > maxBytes) {
      message.error("Image must be smaller than 250KB");
      return Upload.LIST_IGNORE;
    }
    void sendWatermarkFile(file);
    return false; // prevent antd from auto sending
  };

  return (
    <WatermarkSection>
      <SectionHeader>
        <SectionTitle>Watermark</SectionTitle>
        <SectionThemeTag>
          {Theme.friendlyThemeName(currentTheme)}
        </SectionThemeTag>
      </SectionHeader>
      <SectionDescription>
        Upload a watermark image for the current theme. Stored per theme in your
        user settings. The image will be rendered faintly on charts
        (implementation depends on backend and chart rendering pipeline).
      </SectionDescription>

      <PreviewContainer $hasImage={!!effectivePreview}>
        {effectivePreview ? (
          <Tooltip title="Current watermark preview">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={effectivePreview} alt="Theme watermark preview" />
          </Tooltip>
        ) : (
          <EmptyState>No watermark uploaded</EmptyState>
        )}
      </PreviewContainer>

      <Space size="middle" wrap>
        <Upload
          maxCount={1}
          accept="image/png,image/jpeg,image/svg+xml,image/webp"
          showUploadList={false}
          beforeUpload={beforeUpload}
        >
          <Button
            icon={<UploadOutlined />}
            loading={uploading}
            data-testid="upload-watermark-btn"
          >
            {effectivePreview ? "Replace watermark" : "Upload watermark"}
          </Button>
        </Upload>
        {effectivePreview && (
          <Button
            danger
            disabled={uploading}
            onClick={clearWatermark}
            icon={<DeleteOutlined />}
            data-testid="remove-watermark-btn"
          >
            Remove
          </Button>
        )}
      </Space>

      <Guidelines>
        <li>Recommended: transparent PNG / SVG; max size 250KB</li>
        <li>File stored per theme (light / dark / contrast)</li>
        <li>Replacing only affects the active theme</li>
        <li>Removing sends an empty value for this theme’s watermark key</li>
      </Guidelines>
    </WatermarkSection>
  );
}

/* ------------------------------------------------------------------------------------------ */
/* SKELETON PREVIEW                                                                           */
/* ------------------------------------------------------------------------------------------ */

type SkeletonProps = {
  theme: RoseUserSettings["theme"];
};

function Skeleton({ theme }: SkeletonProps) {
  const ScopedTheme = createScopedTheme(theme);
  return (
    <ScopedTheme>
      <SkeletonContainer>
        <SkeletonHeader>
          <SkeletonLogo />
          <SkeletonNav>
            <SkeletonNavItem />
            <SkeletonNavItem />
            <SkeletonNavItem />
          </SkeletonNav>
        </SkeletonHeader>
        <SkeletonBody>
          <SkeletonBodySidebar>
            <SkeletonBodySidebarItem />
            <SkeletonBodySidebarItem />
            <SkeletonBodySidebarItem />
            <SkeletonBodySidebarItem />
            <SkeletonBodySidebarItem />
          </SkeletonBodySidebar>
          <SkeletonBodyMainContainer>
            <SkeletonBodyMainTitle />
            <SkeletonInput />
          </SkeletonBodyMainContainer>
        </SkeletonBody>
      </SkeletonContainer>
    </ScopedTheme>
  );
}

function createScopedTheme(theme: Theme) {
  const css = Theme.createCss(theme);
  // console.log('createScopedTheme', theme, css.split('\n'));
  return styled.div`
    /* stylelint-disable */
    ${css}/* stylelint-enable */
  `;
}

/* ------------------------------------------------------------------------------------------ */
/* STYLES                                                                                     */
/* ------------------------------------------------------------------------------------------ */

const StyledLabelContainer = styled.div`
  background-color: var(${ThemeVarNames.TertiaryBg});
`;

const StyledLabelText = styled.div`
  padding: 10px 0;
  font-size: 14px;
  text-align: center;
  color: var(${ThemeVarNames.PrimaryText});
`;

const StyledContainer = styled.div`
  padding-top: 20px;
  padding-bottom: 20px;
  overflow: hidden;

  img {
    max-width: 230px;
  }
`;
const StyledRadioGroup = styled(Radio.Group)`
  display: inline-flex;
  gap: 48px;

  label.ant-radio-button-wrapper {
    padding: 0;
    height: auto;
    border: none;
    overflow: hidden;
  }

  label.ant-radio-button-wrapper:focus-within {
    box-shadow: none;
  }

  label.ant-radio-button-wrapper {
    border: none;
    overflow: hidden;
  }

  /* there is a conflict between prettier and css lint for this selector */

  /* prettier-ignore */
  label.ant-radio-button-wrapper div[aria-label='label-container'] > *:first-child {
    border: 3px solid transparent;
    border-radius: 11px;
    overflow: hidden;
  }

  label.ant-radio-button-wrapper-checked {
    font-weight: 700;
  }

  /* there is a conflict between prettier and css lint for this selector */

  /* prettier-ignore */
  label.ant-radio-button-wrapper-checked div[aria-label='label-container'] > *:first-child {
    border: 3px solid hsl(357, 83%, 66%) !important;
  }

  label.ant-radio-button-wrapper::before {
    display: none;
  }

  /* Styles for small screens */
  @media (max-width: 1050px) {
    flex-direction: column;
  }
`;

const WatermarkSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-bottom: 4rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(${ThemeVarNames.PrimaryText});
`;

const SectionThemeTag = styled.span`
  background: var(${ThemeVarNames.SecondaryBg});
  color: var(${ThemeVarNames.PrimaryText});
  font-size: 11px;
  padding: 2px 8px 3px;
  border-radius: 12px;
  line-height: 1;
`;

const SectionDescription = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
  color: var(${ThemeVarNames.SecondaryText});
`;

const PreviewContainer = styled.div<{ $hasImage: boolean }>`
  width: 260px;
  height: 120px;
  border: 1px solid var(${ThemeVarNames.Border});
  border-radius: 8px;
  position: relative;
  background:
    linear-gradient(45deg, #0000000a 25%, transparent 25%) 0 0 / 16px 16px,
    linear-gradient(-45deg, #0000000a 25%, transparent 25%) 0 0 / 16px 16px,
    linear-gradient(45deg, transparent 75%, #0000000a 75%) 0 0 / 16px 16px,
    linear-gradient(-45deg, transparent 75%, #0000000a 75%) 0 0 / 16px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    opacity: 0.75;
  }
`;

const EmptyState = styled.div`
  font-size: 12px;
  color: var(${ThemeVarNames.SecondaryText});
  text-align: center;
  padding: 0 12px;
`;

const Guidelines = styled.ul`
  margin: 4px 0 0;
  padding-left: 18px;
  font-size: 11px;
  color: var(${ThemeVarNames.SecondaryText});
  display: flex;
  flex-direction: column;
  gap: 2px;
  li {
    list-style: disc;
  }
`;

/* Skeleton styles */

const SkeletonContainer = styled.div`
  position: relative;
  width: 210px;
  height: 147px;
  background-color: var(${ThemeVarNames.PrimaryBg});
`;

const SkeletonHeader = styled.div`
  position: relative;
  width: 100%;
  height: 16%;
  padding: 3%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: var(${ThemeVarNames.SecondaryBg});
`;

const SkeletonLogo = styled.div`
  border-radius: 50%;
  height: 100%;
  aspect-ratio: 1 / 1;
  background-color: var(${ThemeVarNames.PrimaryBrand});
`;

const SkeletonNav = styled.div`
  height: 100%;
  flex: 1;
  display: flex;
  justify-content: flex-end;
  gap: 3%;
  padding: 1.5%;

  & > div:first-child {
    background-color: var(${ThemeVarNames.Action});
  }
`;

const SkeletonNavItem = styled.div`
  height: 100%;
  width: 20%;
  border-radius: 8px;
  background-color: var(${ThemeVarNames.PrimaryText});
`;

const SkeletonBody = styled.div`
  height: 85%;
  display: flex;
  padding-top: 6%;
`;

const SkeletonBodySidebar = styled.div`
  width: 10%;
  height: 100%;
  padding: 3%;
  display: flex;
  flex-direction: column;
  gap: 6%;
`;

const SkeletonBodySidebarItem = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  background-color: var(${ThemeVarNames.Action});
`;

const SkeletonBodyMainContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8%;
`;

const SkeletonBodyMainTitle = styled.div`
  width: 20%;
  height: 4%;
  border-radius: 8px;
  background-color: var(${ThemeVarNames.PrimaryText});
`;

const SkeletonInput = styled.div`
  height: 8%;
  width: 80%;
  border-radius: 8px;
  background-color: var(${ThemeVarNames.InputAlternative});
`;
